/*
  carousel-ada.js
  ----------------
  Reusable accessibility helpers for auto-rotating carousels, ported from the
  Hydrogen store's useCarouselAutoPlay hook + CarouselPausePlayButton +
  CarouselLiveRegion components so the same WCAG 2.2.2 (Pause, Stop, Hide)
  treatment can be applied to any Liquid carousel.

  Nothing here is carousel-specific: each carousel supplies its own onPlay /
  onPause behaviour (e.g. clearing a setInterval, or freezing a third-party
  widget) and calls announce() when the visible slide changes. Exposed as
  window.CarouselADA.

  Usage sketch:
    var c = CarouselADA.createController({
      onPlay:  function () { startAutoplay(); },
      onPause: function () { stopAutoplay(); },
      playingMessage: 'Carousel playing',
      pausedMessage:  'Carousel paused',
    });
    var btn = CarouselADA.createPausePlayButton({ onToggle: c.toggle });
    c.setButton(btn);
    host.appendChild(c.liveRegion);   // sr-only announcer
    host.appendChild(btn);            // visible toggle
    CarouselADA.wireFocusPause(host, c);
*/
(function () {
  'use strict';

  var ICONS = {
    pause:
      '<svg class="carousel-ada-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9-3a1 1 0 1 0-2 0v6a1 1 0 1 0 2 0V9Zm4 0a1 1 0 1 0-2 0v6a1 1 0 1 0 2 0V9Z"/>' +
      '</svg>',
    play:
      '<svg class="carousel-ada-icon" viewBox="-2 -2 24 24" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M 7.4999997,14.5 14.5,10 7.4999997,5.5000002 Z M 9.9999999,20 Q 7.9249997,20 6.1,19.2125 4.275,18.425 2.925,17.075 1.575,15.725 0.7875,13.9 0,12.075 0,10 0,7.9250002 0.7875,6.1000002 1.575,4.275 2.925,2.925 4.275,1.575 6.1,0.7875 7.9249997,0 9.9999999,0 12.075,0 13.9,0.7875 15.725,1.575 17.075,2.925 18.425,4.275 19.2125,6.1000002 20,7.9250002 20,10 q 0,2.075 -0.7875,3.9 -0.7875,1.825 -2.1375,3.175 -1.35,1.35 -3.175,2.1375 Q 12.075,20 9.9999999,20 Z m 0,-2 Q 13.35,18 15.675,15.675 18,13.35 18,10 18,6.6500002 15.675,4.325 13.35,2 9.9999999,2 6.6499997,2 4.325,4.325 2,6.6500002 2,10 2,13.35 4.325,15.675 6.6499997,18 9.9999999,18 Z m 0,-8 z"/>' +
      '</svg>',
  };

  function prefersReducedMotion() {
    return !!(
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function createPausePlayButton(opts) {
    opts = opts || {};
    var playLabel = opts.playLabel || 'Play carousel';
    var pauseLabel = opts.pauseLabel || 'Pause carousel';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'carousel-ada-button' + (opts.className ? ' ' + opts.className : '');
    btn.setAttribute('data-carousel-autoplay-toggle', 'true');
    if (opts.iconFill) btn.style.color = opts.iconFill;

    function setPlaying(isPlaying) {
      btn.setAttribute('aria-pressed', String(!isPlaying));
      btn.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
      btn.innerHTML = isPlaying ? ICONS.pause : ICONS.play;
    }

    setPlaying(opts.isPlaying !== false);
    if (typeof opts.onToggle === 'function') {
      btn.addEventListener('click', opts.onToggle);
    }
    btn.setPlaying = setPlaying;
    return btn;
  }

  function createLiveRegion(opts) {
    opts = opts || {};
    var region = document.createElement('div');
    region.className = 'sr-only' + (opts.className ? ' ' + opts.className : '');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    return {
      el: region,
      announce: function (message) {
        region.textContent = message == null ? '' : String(message);
      },
    };
  }

  function createController(opts) {
    opts = opts || {};
    var playingMessage = opts.playingMessage || 'Carousel playing';
    var pausedMessage = opts.pausedMessage || 'Carousel paused';
    var live = createLiveRegion({ className: opts.liveRegionClassName });
    var button = null;
    var isPlaying = opts.initialPlaying !== false;

    function reflect() {
      if (button) button.setPlaying(isPlaying);
    }

    var api = {
      liveRegion: live.el,
      announce: live.announce,
      isPlaying: function () {
        return isPlaying;
      },
      setButton: function (b) {
        button = b;
        reflect();
      },
      play: function (settings) {
        if (isPlaying) return;
        isPlaying = true;
        reflect();
        if (typeof opts.onPlay === 'function') opts.onPlay();
        if (!(settings && settings.silent)) live.announce(playingMessage);
      },
      pause: function (settings) {
        if (!isPlaying) return;
        isPlaying = false;
        reflect();
        if (typeof opts.onPause === 'function') opts.onPause();
        if (!(settings && settings.silent)) live.announce(pausedMessage);
      },
      toggle: function () {
        if (isPlaying) api.pause();
        else api.play();
      },
    };
    return api;
  }

  function wireFocusPause(container, controller) {
    if (!container) return;
    container.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[data-carousel-autoplay-toggle]')) return;
      controller.pause();
    });
  }

  window.CarouselADA = {
    icons: ICONS,
    prefersReducedMotion: prefersReducedMotion,
    createPausePlayButton: createPausePlayButton,
    createLiveRegion: createLiveRegion,
    createController: createController,
    wireFocusPause: wireFocusPause,
  };
})();
